import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import * as React from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { HelmetProvider } from 'react-helmet-async';

import { AuthLoader } from '@/features/auth/components/auth-loader';
import { OrganizationPlansDialog } from '@/features/subscriptions/components/organization-plans-dialog';
import { PaywallDialog } from '@/features/subscriptions/components/paywall-dialog';
import {
  PaywallProvider,
  usePaywallContext,
} from '@/features/subscriptions/context/paywall-context';
import { ReactQueryClientProvider } from '@/lib/react-query';
import { MainErrorFallback } from '@/shared/components/errors/main';
import { LanguageSync } from '@/shared/components/language-sync';
import { Spinner } from '@/shared/components/ui/spinner';
import { Toaster } from '@/shared/components/ui/toaster';

const PaywallDialogWrapper = () => {
  const { isOpen, context, projectId, closePaywall } = usePaywallContext();
  if (!context) return null;

  return (
    <PaywallDialog
      open={isOpen}
      onOpenChange={(open) => {
        if (!open) {
          closePaywall();
        }
      }}
      context={context}
      projectId={projectId}
    />
  );
};

type AppProviderProps = {
  children: React.ReactNode;
};

export const AppProvider = ({ children }: AppProviderProps) => {
  return (
    <React.Suspense
      fallback={
        <div className="flex h-screen w-screen items-center justify-center">
          <Spinner size="xl" />
        </div>
      }
    >
      <ErrorBoundary FallbackComponent={MainErrorFallback}>
        <HelmetProvider>
          <ReactQueryClientProvider>
            {import.meta.env.DEV && <ReactQueryDevtools />}
            <Toaster />
            <PaywallProvider>
              <PaywallDialogWrapper />
              <OrganizationPlansDialog />
              <AuthLoader
                renderLoading={() => (
                  <div className="flex h-screen w-screen items-center justify-center">
                    <Spinner size="xl" />
                  </div>
                )}
              >
                <LanguageSync />
                {children}
              </AuthLoader>
            </PaywallProvider>
          </ReactQueryClientProvider>
        </HelmetProvider>
      </ErrorBoundary>
    </React.Suspense>
  );
};
